import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, ArrowLeft } from 'lucide-react';
import { toast } from '@/stores/toastStore';
import api from '@/services/api';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import { useTranslation } from 'react-i18next';

const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPasswordPage() {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation('auth');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);

    try {
      await api.post('/auth/request-password-reset', { email: data.email });
      // Generic message for security (prevent email enumeration)
      toast.success('If an account exists with this email, you will receive password reset instructions shortly.');
      reset(); // Clear the form
    } catch (error: any) {
      // Show generic error to prevent email enumeration
      toast.error('Unable to process request. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-white to-purple-50/60 fixed inset-0 overflow-auto">
      <div className="w-full max-w-md my-auto">
        {/* Back to Login Link */}
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{t('forgotPassword.backToLogin')}</span>
        </Link>

        {/* Title */}
        <div className="text-center mb-8 animate-slide-down">
          <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{t('forgotPassword.title')}</h1>
          <p className="text-gray-600">
            {t('forgotPassword.subtitle')}
          </p>
        </div>

        {/* Form */}
        <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-8 shadow-lg animate-slide-up">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <Input
              {...register('email')}
              type="email"
              label={t('forgotPassword.emailLabel')}
              placeholder={t('forgotPassword.emailPlaceholder')}
              error={errors.email?.message}
              icon={<Mail className="w-5 h-5" />}
              autoComplete="email"
              disabled={isLoading}
            />

            <Button
              type="submit"
              variant="primary"
              fullWidth
              isLoading={isLoading}
              className="group"
            >
              {!isLoading && <Mail className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />}
              {t('forgotPassword.submitButton')}
            </Button>
          </form>
        </div>

        {/* Security Note */}
        <div className="mt-6 bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-4 shadow-sm animate-slide-up delay-100">
          <p className="text-xs text-gray-600 text-center">
            {t('forgotPassword.securityNote')}
          </p>
        </div>
      </div>
    </div>
  );
}
